import { useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { CompanyChip, KeyValues, Notice, Pipeline, StatusPill, XmlView } from "../../design/components";
import { ApiError } from "../../api/client";
import { useDocument, useDocumentArchive, useDocumentRaw } from "../../api/queries";
import { isHeldStage, useLandInbound, useResolveInbound } from "../../api/inbound";
import { useAuth } from "../../auth/AuthProvider";
import { INBOUND_STAGES, inboundStageIndex } from "../../domain/stages";
import { dateTime, omr } from "../../lib/format";
import { statePill } from "../documents/DocumentRow";
import { EventsPanel } from "../documents/inspector/EventsPanel";
import { OutcomePanel } from "../documents/inspector/OutcomePanel";
import { ValidationPanel } from "../documents/inspector/ValidationPanel";
import { INBOUND_STAGE_STRIP } from "./InFlightTable";
import { ERP_DRAFT_STATE } from "./LandedTable";
import { RejectDialog } from "./RejectDialog";
import styles from "./inbound.module.css";

const ARCHIVED_INDEX = INBOUND_STAGES.indexOf("archived");

/* An off-ramp has no pip of its own, so it marks the stage it stopped at as failed. */
const OFF_RAMP: Record<string, number> = { held_unrouted: 1, held_exception: 2, rejected: 1 };

function pipelinePosition(stage: string): { current: number; failedAt?: number } {
  const index = inboundStageIndex(stage);
  if (index >= 0) return { current: index };
  const stopped = OFF_RAMP[stage] ?? 0;
  return { current: stopped, failedAt: stopped };
}

function amount(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : omr(Number(value));
}

const RESOLVE_FAILED: Record<string, string> = {
  NOT_ON_A_HOLD_STAGE: "This document is no longer held, so it cannot be retried.",
};

const RESOLVE_FAILED_KEY: Record<string, string> = {
  NOT_ON_A_HOLD_STAGE: "inbound.doc.resolveFailedNotOnHold",
};

const LAND_FAILED: Record<string, string> = {
  NOT_AT_MAPPED: "This document is not at the mapped stage, so no draft can be created.",
  FILE_METHOD_HAS_NO_ERP:
    "This company receives documents by secure file transfer, so the hub has no ERP to create a draft in.",
};

const LAND_FAILED_KEY: Record<string, string> = {
  NOT_AT_MAPPED: "inbound.doc.landFailedNotAtMapped",
  FILE_METHOD_HAS_NO_ERP: "inbound.doc.landFailedFileMethod",
};

function actionMessage(
  error: unknown,
  map: Record<string, string>,
  keyMap: Record<string, string>,
  fallback: string,
  fallbackKey: string,
  t: (key: string, fallback: string) => string,
): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return t("inbound.doc.forbiddenDraft", "Only a company administrator can create the draft.");
    }
    const key = keyMap[error.code];
    const mapped = map[error.code];
    if (key && mapped) return t(key, mapped);
    return t(fallbackKey, fallback);
  }
  return t(fallbackKey, fallback);
}

export function InboundDocumentPage() {
  const { t } = useTranslation();
  const { docId } = useParams();
  const { me } = useAuth();
  const document = useDocument(docId);
  const raw = useDocumentRaw(docId);
  const sealedStage = inboundStageIndex(document.data?.stage ?? "") >= ARCHIVED_INDEX;
  const archive = useDocumentArchive(docId, sealedStage);
  const resolve = useResolveInbound(docId ?? "");
  const land = useLandInbound(docId ?? "");
  const [rejecting, setRejecting] = useState(false);

  if (document.isError) {
    const missing = document.error instanceof ApiError && document.error.status === 404;
    return (
      <Notice kind={missing ? "info" : "fail"}>
        <div>
          {missing
            ? t("inbound.doc.notAvailable", "This document is not available")
            : t("inbound.doc.loadFailed", "This document could not be loaded.")}
        </div>
        <Link className="link" to="/inbound">
          {t("inbound.doc.backToDocuments", "Back to received documents")}
        </Link>
      </Notice>
    );
  }

  const doc = document.data;
  if (!doc || !docId) return <div className="page-loading" />;

  const held = isHeldStage(doc.stage);
  // Ruling 79: only an entity admin may post into an ERP, so a group admin never sees this.
  const canCreateDraft = doc.stage === "mapped" && me?.role === "entity_admin";
  const { current, failedAt } = pipelinePosition(doc.stage);
  const totals = doc.totals;
  const sealed = archive.data;

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{doc.invoiceNumber ?? doc.id}</h1>
          <div className={styles.headMeta}>
            {doc.entityId ? (
              <CompanyChip entityId={doc.entityId} name={doc.entityName ?? ""} code={doc.entityCode ?? ""} />
            ) : (
              <span className={styles.subtle}>{t("inbound.doc.notRouted", "Not yet routed to a company")}</span>
            )}
            <StatusPill {...statePill(doc.state)} />
            <span className={styles.subtle}>
              {t("inbound.doc.inboundDocType", "Inbound · {{docType}}", { docType: doc.docType })}
            </span>
          </div>
        </div>
        <div className="row-tight">
          {held ? (
            <>
              <button
                type="button"
                className="btn btn-sm"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate({ action: "retry", note: "" })}
              >
                {t("inbound.doc.retry", "Retry")}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={resolve.isPending}
                onClick={() => setRejecting(true)}
              >
                {t("inbound.doc.reject", "Reject")}
              </button>
            </>
          ) : null}
          {canCreateDraft ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={land.isPending}
              onClick={() => land.mutate()}
            >
              {t("inbound.doc.createDraftNow", "Create draft now")}
            </button>
          ) : null}
        </div>
      </div>

      {resolve.isError ? (
        <div className={styles.pageNotice}>
          <Notice kind="fail">
            {actionMessage(
              resolve.error,
              RESOLVE_FAILED,
              RESOLVE_FAILED_KEY,
              "This document could not be resolved.",
              "inbound.doc.resolveFailedGeneric",
              t,
            )}
          </Notice>
        </div>
      ) : null}
      {land.isError ? (
        <div className={styles.pageNotice}>
          <Notice kind="fail">
            {actionMessage(
              land.error,
              LAND_FAILED,
              LAND_FAILED_KEY,
              "The draft could not be created.",
              "inbound.doc.landFailedGeneric",
              t,
            )}
          </Notice>
        </div>
      ) : null}

      <section className="card">
        <div className="card-head">
          <h2 className="grow">{t("inbound.doc.summary", "Summary")}</h2>
        </div>
        <div className="card-body">
          <Pipeline
            current={current}
            stages={INBOUND_STAGE_STRIP}
            // Landed (a draft created, or a Method 3 file delivered) is the end of the line.
            complete={doc.state === "landed"}
            {...(failedAt === undefined ? {} : { failedAt })}
          />
          <KeyValues
            pairs={[
              [t("inbound.doc.supplier", "Supplier"), doc.counterparty ?? "—"],
              [
                t("inbound.doc.receiverIdentifier", "Receiver identifier"),
                <span className="mono">{doc.participantTo ?? "—"}</span>,
              ],
              [t("inbound.doc.invoiceNumber", "Invoice number"), doc.invoiceNumber ?? "—"],
              [t("inbound.doc.uuid", "UUID"), <span className="mono">{doc.uuid ?? "—"}</span>],
              [t("inbound.doc.received", "Received"), dateTime(doc.createdAt)],
              [t("inbound.doc.lastChange", "Last change"), dateTime(doc.updatedAt)],
              [t("inbound.doc.net", "Net"), amount(totals?.taxExclusive)],
              [t("inbound.doc.vat", "VAT"), amount(totals?.taxAmount)],
              [t("inbound.doc.total", "Total"), `${amount(totals?.payable)} ${doc.currency ?? ""}`.trim()],
            ]}
          />
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="grow">{t("inbound.doc.receivedDocument", "Received document")}</h2>
        </div>
        <div className="card-body">
          {raw.isError ? (
            <Notice kind="fail">
              {t("inbound.doc.receivedDocumentLoadFailed", "The received document could not be loaded.")}
            </Notice>
          ) : raw.data ? (
            <XmlView src={raw.data} />
          ) : null}
        </div>
      </section>

      <ValidationPanel docId={docId} />

      <section className="card">
        <div className="card-head">
          <h2 className="grow">{t("inbound.doc.archive", "Archive")}</h2>
        </div>
        <div className="card-body">
          {sealed ? (
            <KeyValues
              pairs={[
                [t("inbound.doc.sealedXml", "Sealed XML"), <span className={styles.sha}>{sealed.xmlSha256}</span>],
                [t("inbound.doc.sealedAt", "Sealed at"), dateTime(sealed.sealedAt)],
              ]}
            />
          ) : (
            <Notice kind="info">{t("inbound.doc.notSealedYet", "This document is not sealed yet")}</Notice>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="grow">{t("inbound.doc.draftInErp", "Draft in the ERP")}</h2>
        </div>
        <div className="card-body">
          <KeyValues
            pairs={[
              [t("inbound.doc.erpReference", "ERP reference"), doc.erpDocId ?? "—"],
              [
                t("inbound.doc.state", "State"),
                doc.stage === "landed_draft"
                  ? t("inbound.doc.erpDraftState", ERP_DRAFT_STATE)
                  : t("inbound.doc.noDraftCreatedYet", "No draft created yet"),
              ],
            ]}
          />
          {doc.errorCode ? (
            <Notice kind="fail">
              <div className="mono">{doc.errorCode}</div>
              {doc.errorMessage ? <div>{doc.errorMessage}</div> : null}
              {held ? (
                <div>
                  {t("inbound.doc.assignOrReject", "Assign the document to a company, or reject it with a note.")}
                </div>
              ) : null}
            </Notice>
          ) : null}
        </div>
      </section>

      <OutcomePanel docId={docId} />

      <EventsPanel docId={docId} title={t("inbound.doc.timeline", "Timeline")} />

      {rejecting ? (
        <RejectDialog
          invoiceNumber={doc.invoiceNumber ?? doc.id}
          pending={resolve.isPending}
          onClose={() => setRejecting(false)}
          onConfirm={(note) => {
            setRejecting(false);
            resolve.mutate({ action: "reject", note });
          }}
        />
      ) : null}
    </>
  );
}
